import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMovieDetails } from '../utils/api';
import { useFavorites } from '../utils/FavoritesContext';
import './MovieDetails.css';

const MovieDetails = () => {
    const { imdbID } = useParams();
    const navigate = useNavigate();
    const [movie, setMovie] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isFavorite, toggleFavorite } = useFavorites();

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                setLoading(true);
                const data = await getMovieDetails(imdbID);
                setMovie(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (imdbID) {
            fetchDetails();
        }
    }, [imdbID]);

    useEffect(() => {
        if (movie) {
            document.title = `${movie.Title} (${movie.Year}) - MovieHub`;
        } else {
            document.title = 'Movie Details - MovieHub';
        }
    }, [movie]);

    if (loading) {
        return (
            <div className="details-loading">
                <div className="loader"></div>
            </div>
        );
    }

    if (error || !movie) {
        return (
            <div className="details-error">
                <div className="container">
                    <h2>Error Loading Movie</h2>
                    <p>{error || 'Movie not found'}</p>
                    <button onClick={() => navigate('/')} className="back-btn">
                        Go Back Home
                    </button>
                </div>
            </div>
        );
    }

    const fallbackImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="450" viewBox="0 0 300 450"%3E%3Crect width="300" height="450" fill="%23242424"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Inter, sans-serif" font-size="20" fill="%238c8c8c"%3ENo Poster%3C/text%3E%3C/svg%3E';

    return (
        <div className="movie-details fade-in">
            <div className="backdrop-overlay"
                style={{ backgroundImage: `url(${movie.Poster !== 'N/A' ? movie.Poster : ''})` }}>
            </div>

            <div className="container relative-z">
                <button onClick={() => navigate(-1)} className="back-link">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>

                <div className="details-content">
                    <div className="poster-section">
                        <img
                            src={movie.Poster !== 'N/A' ? movie.Poster : fallbackImage}
                            alt={movie.Title}
                            className="details-poster"
                        />
                    </div>

                    <div className="info-section">
                        <div className="header-info">
                            <h1 className="details-title">{movie.Title}</h1>

                            <div className="meta-tags">
                                <span className="tag year">{movie.Year}</span>
                                <span className="tag rated">{movie.Rated}</span>
                                <span className="tag runtime">{movie.Runtime}</span>
                            </div>

                            <div className="ratings">
                                {movie.Ratings && movie.Ratings.map((rating, index) => (
                                    <div key={index} className="rating-item">
                                        <span className="rating-source">{rating.Source}</span>
                                        <span className="rating-value">{rating.Value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="action-buttons">
                            <button
                                className={`action-btn favorite ${isFavorite(movie.imdbID) ? 'active' : ''}`}
                                onClick={() => toggleFavorite(movie)}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite(movie.imdbID) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                </svg>
                                {isFavorite(movie.imdbID) ? 'Remove from Favorites' : 'Add to Favorites'}
                            </button>

                            <a
                                href={`https://www.imdb.com/title/${movie.imdbID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-btn imdb"
                            >
                                View on IMDb
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </a>
                        </div>

                        <div className="plot-section">
                            <h3>Plot</h3>
                            <p>{movie.Plot}</p>
                        </div>

                        <div className="details-grid">
                            <div className="grid-item">
                                <h3>Genre</h3>
                                <p>{movie.Genre}</p>
                            </div>
                            <div className="grid-item">
                                <h3>Director</h3>
                                <p>{movie.Director}</p>
                            </div>
                            <div className="grid-item">
                                <h3>Actors</h3>
                                <p>{movie.Actors}</p>
                            </div>
                            <div className="grid-item">
                                <h3>Awards</h3>
                                <p>{movie.Awards}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MovieDetails;
